import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { User, AppRole, TradeType, AccountMode, SubscriptionTier } from './types';

/**
 * Firebase Authentication Helper Functions
 * Provides Supabase-compatible authentication API
 * 
 * IMPORTANT: Personal Account Handling
 * ====================================
 * This app supports two account modes: Personal and Business
 * 
 * - Personal accounts: Solo contractors with NO roles array (empty [])
 *   They automatically get admin/manager privileges
 * 
 * - Business accounts: Companies with role-based permissions
 *   Roles: admin, manager, crew_leader, technician
 * 
 * ALWAYS use getUserAccessInfo() instead of checking roles directly!
 * This function handles Personal accounts automatically and returns
 * computed permission flags (isAdmin, isManager, isEmployee, etc.)
 * 
 * DO NOT check roles.includes('admin') - use accessInfo.isAdmin instead
 * DO NOT check accountMode === 'personal' everywhere - centralize it here
 */

export interface SignUpOptions {
  email: string;
  password: string;
  options?: {
    data?: {
      fullName?: string;
      companyName?: string;
      user_type?: string;
      trade_type?: string | null;
      custom_trade_type?: string | null;
      account_mode?: AccountMode;
      is_service_provider?: boolean;
      phone?: string;
      // Enterprise signup fields
      team_size?: string;
      subscription_tier?: string;
      signup_timestamp?: string;
      agreed_to_terms?: boolean;
      agreed_to_privacy?: boolean;
      // Business location
      business_country?: string;
      business_state?: string | null;
    };
    emailRedirectTo?: string;
  };
}

export interface SignInOptions {
  email: string;
  password: string;
}

/**
 * Sign up a new user with email and password
 */
export async function signUp({ email, password, options }: SignUpOptions) {
  try {
    // Create user with Firebase JS SDK
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (!user) {
      throw new Error('Failed to create user');
    }

    // Update user profile with display name
    if (options?.data?.fullName) {
      await updateProfile(user, {
        displayName: options.data.fullName
      });
    }

    // Determine trade type
    let finalTradeType: TradeType = 'electrician'; // default
    if (options?.data?.trade_type) {
      if (options.data.trade_type === 'other' && options.data.custom_trade_type) {
        // For 'other', we'll use electrician as default and store custom in a separate field
        finalTradeType = 'electrician';
      } else {
        // Map the trade type to our enum
        const tradeMap: Record<string, TradeType> = {
          'electrician': 'electrician',
          'plumber': 'plumber',
          'framer': 'framer',
          'hvac': 'hvac',
          'gas': 'gas_installer',
          'gas_installer': 'gas_installer',
          'roofing': 'roofing',
          'general': 'general',
        };
        finalTradeType = tradeMap[options.data.trade_type] || 'electrician';
      }
    }

    // Check if this is a client signup
    const isClient = !options?.data?.is_service_provider;

    if (isClient) {
      // Create simplified client document in clients collection
      const clientData = {
        uid: user.uid,
        email: email,
        fullName: options?.data?.fullName || '',
        companyName: options?.data?.companyName || null,
        phone: options?.data?.phone || null,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      };

      console.log('Creating client document with data:', clientData); // Debug
      await setDoc(doc(db, 'clients', user.uid), clientData);
      console.log('Client document created successfully'); // Debug
    } else {
      // Service provider - create full user document
      const roles: AppRole[] = [];
      const accountMode = options?.data?.account_mode || 'personal';
      
      // Personal accounts don't need roles - they have full access
      // Business/Professional/Enterprise accounts need role-based permissions
      // Service providers who sign up directly get admin role
      if (accountMode === 'business' || accountMode === 'enterprise' || accountMode === 'professional') {
        // Business account service providers who sign up get admin role
        // Other roles (manager, crew_leader, technician) can only be assigned by admin
        roles.push('admin');
      }
      // Note: Personal accounts get empty roles array - permissions are handled by account_mode

      // Determine subscription tier
      const teamSize = options?.data?.team_size || '2-5';
      const subscriptionTier: SubscriptionTier = teamSize === '20+' ? 'enterprise' : teamSize === '6-20' ? 'business' : 'professional';

      // Create user document in Firestore
      const userData: Partial<User> = {
        uid: user.uid,
        email: email,
        fullName: options?.data?.fullName || '',
        companyName: options?.data?.companyName || '',
        tradeType: finalTradeType,
        accountMode: accountMode,
        roles: roles,
        // Enterprise signup fields
        teamSize: teamSize,
        subscriptionTier: subscriptionTier,
        isPrimaryAdmin: true, // Signup user is always the primary admin
        // Business location
        businessCountry: options?.data?.business_country || undefined,
        businessState: options?.data?.business_state || undefined,
        // Consent tracking
        signupTimestamp: options?.data?.signup_timestamp || new Date().toISOString(),
        agreedToTerms: options?.data?.agreed_to_terms || false,
        agreedToPrivacy: options?.data?.agreed_to_privacy || false,
        subscription: {
          // Service providers start with trial subscription
          status: 'trialing',
          plan: subscriptionTier,
          tier: subscriptionTier,
          trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000) as any, // 14 days
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          currentPeriodEnd: null,
        },
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      };

      console.log('Creating user document with data:', userData); // Debug
      await setDoc(doc(db, 'users', user.uid), userData);
      console.log('User document created successfully'); // Debug
    }

    // Send email verification only for service providers using SendGrid extension
    if (options?.data?.is_service_provider) {
      // Generate verification token
      const verificationToken = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Token expires in 24 hours
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

      // Mark user as not verified in user document
      await setDoc(doc(db, 'users', user.uid), {
        emailVerified: false,
      }, { merge: true }); // Use merge to avoid overwriting subscription object

      // Store verification token in separate email_verifications collection
      // This allows unauthenticated users to verify via token lookup
      await setDoc(doc(db, 'email_verifications', verificationToken), {
        token: verificationToken,
        userId: user.uid,
        email: email,
        expiresAt: Timestamp.fromDate(expiresAt),
        verified: false,
        createdAt: serverTimestamp(),
      });
      
      // Create verification URL - app is hosted at app.normiz.com
      // Using /email-verify path to ensure it opens in Safari (not universal linked)
      const baseUrl = 'https://app.normiz.com';
      const verificationUrl = `${baseUrl}/email-verify?token=${verificationToken}`;

      // Queue verification email via Firebase Extension
      // Logo hosted at app.normiz.com - put the file at /public/images/email-logo.png (60x60px)
      const logoUrl = 'https://app.normiz.com/images/email-logo.png';

      await addDoc(collection(db, 'mail'), {
        to: email,
        from: 'Normiz <noreply@normiz.com>',
        replyTo: 'support@normiz.com',
        headers: {
          'List-Unsubscribe': '<mailto:unsubscribe@normiz.com>',
        },
        message: {
          subject: 'Verify your Normiz account',
          text: `Welcome to Normiz!

Thank you for signing up! Please verify your email address by clicking the link below:

${verificationUrl}

This verification link will expire in 24 hours for security reasons.

If you didn't create an account with Normiz, you can safely ignore this email.

Best regards,
The Normiz Team

---
This email was sent by Normiz. Questions? Contact us at support@normiz.com`,
          html: `
            <!DOCTYPE html>
            <html lang="en">
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <meta http-equiv="X-UA-Compatible" content="IE=edge">
                <title>Verify Your Email - Normiz</title>
              </head>
              <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
                <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f5f5f5; padding: 20px 0;">
                  <tr>
                    <td align="center">
                      <table role="presentation" cellpadding="0" cellspacing="0" width="600" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                        <tr>
                          <td style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); padding: 30px 40px; text-align: center;">
                            <img src="${logoUrl}" alt="Normiz" width="60" height="60" style="display: block; margin: 0 auto; border: 0; outline: none;">
                            <h1 style="color: #ffffff; font-size: 24px; margin: 15px 0 0 0; font-weight: 600;">Normiz</h1>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 40px 40px 30px 40px; color: #333333; line-height: 1.6;">
                            <h2 style="color: #1f2937; font-size: 22px; margin: 0 0 20px 0; font-weight: 600;">Verify Your Email Address</h2>
                            <p style="margin: 0 0 15px 0; font-size: 15px;">Thank you for signing up for Normiz! To complete your registration and start your 14-day free trial, please verify your email address by clicking the button below.</p>
                            <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin: 25px 0;">
                              <tr>
                                <td align="center">
                                  <!--[if mso]>
                                  <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${verificationUrl}" style="height:48px;v-text-anchor:middle;width:220px;" arcsize="13%" fillcolor="#2563eb">
                                  <w:anchorlock/>
                                  <center style="color:#ffffff;font-family:sans-serif;font-size:16px;font-weight:bold;">Verify Email Address</center>
                                  </v:roundrect>
                                  <![endif]-->
                                  <!--[if !mso]><!-->
                                  <a href="${verificationUrl}" style="display: inline-block; background-color: #2563eb; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600; box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);">Verify Email Address</a>
                                  <!--<![endif]-->
                                </td>
                              </tr>
                            </table>
                            <p style="margin: 0 0 15px 0; font-size: 14px; color: #6b7280;">If the button doesn't work, copy and paste this link into your browser:</p>
                            <p style="background: #f3f4f6; padding: 12px; border-radius: 4px; word-break: break-all; font-size: 13px; color: #2563eb; margin: 0 0 25px 0;">${verificationUrl}</p>
                            <p style="margin: 0 0 15px 0; font-size: 14px; color: #6b7280;">If you didn't create an account with Normiz, you can safely ignore this email.</p>
                            <p style="margin: 0 0 15px 0; font-size: 14px; color: #6b7280;">This verification link will expire in 24 hours for security reasons.</p>
                            <p style="margin: 0; font-size: 15px;">Best regards,<br><strong>The Normiz Team</strong></p>
                          </td>
                        </tr>
                        <tr>
                          <td style="background-color: #f8f9fa; padding: 30px 40px; border-top: 1px solid #e5e7eb;">
                            <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                              <tr>
                                <td style="text-align: center; color: #6b7280; font-size: 13px; line-height: 1.6;">
                                  <p style="margin: 0 0 10px 0;">Powered by <strong>Normiz</strong> - Enterprise Operations Management</p>
                                  <p style="margin: 0 0 15px 0; font-size: 12px;">Questions? Contact us at <a href="mailto:support@normiz.com" style="color: #2563eb; text-decoration: none;">support@normiz.com</a></p>
                                  <p style="margin: 0; font-size: 11px; color: #9ca3af;">
                                    <a href="https://normiz.com/privacy" style="color: #6b7280; text-decoration: none; margin: 0 8px;">Privacy Policy</a> |
                                    <a href="https://normiz.com/terms" style="color: #6b7280; text-decoration: none; margin: 0 8px;">Terms of Service</a>
                                  </p>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>
                      <table role="presentation" cellpadding="0" cellspacing="0" width="600" style="margin-top: 20px;">
                        <tr>
                          <td style="text-align: center; color: #9ca3af; font-size: 11px; line-height: 1.5; padding: 0 20px;">
                            <p style="margin: 0;">\u00A9 ${new Date().getFullYear()} Normiz. All rights reserved.</p>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </body>
            </html>
          `,
        },
      });
    }

    return {
      data: {
        user,
        session: null
      },
      error: null
    };
  } catch (error: any) {
    return {
      data: { user: null, session: null },
      error: { message: error.message, code: error.code }
    };
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithPassword({ email, password }: SignInOptions) {
  try {
    console.log('🔄 signInWithPassword starting...');

    // Use JS SDK with timeout
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        console.error('⏰ Sign in timeout after 30 seconds');
        reject(new Error('Sign in timeout - please check your connection'));
      }, 30000);
    });

    const signInPromise = signInWithEmailAndPassword(auth, email, password);
    const userCredential = await Promise.race([signInPromise, timeoutPromise]);
    const user = userCredential.user;
    console.log('✅ Sign in successful:', user?.uid);

    if (!user) {
      throw new Error('Sign in failed - no user returned');
    }

    return {
      data: {
        user,
        session: null
      },
      error: null
    };
  } catch (error: any) {
    console.error('❌ Sign in error:', error.message, error.code);
    return {
      data: { user: null, session: null },
      error: { message: error.message, code: error.code }
    };
  }
}

/**
 * Sign out the current user
 */
export async function signOut() {
  try {
    // Clear subscription session flags on sign out
    sessionStorage.removeItem('subscription_active');
    sessionStorage.removeItem('just_completed_payment');

    await firebaseSignOut(auth);

    return { error: null };
  } catch (error: any) {
    return { error: { message: error.message } };
  }
}

/**
 * Get current session/user
 */
export async function getSession() {
  return new Promise<{ data: { session: any; user: FirebaseUser | null }; error: null }>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve({
        data: {
          session: user ? { user } : null,
          user
        },
        error: null
      });
    });
  });
}

/**
 * Get current user
 */
export async function getUser() {
  const currentUser = auth.currentUser;
  return {
    data: { user: currentUser },
    error: null
  };
}

/**
 * Get user roles from Firestore
 */
export async function getUserRoles(userId: string): Promise<AppRole[]> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      const roles = userData.roles || [];
      return roles;
    }
    return [];
  } catch (error) {
    console.error('Error fetching user roles:', error);
    return [];
  }
}

/**
 * Get user account mode from Firestore
 */
export async function getAccountMode(userId: string): Promise<AccountMode> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      return userData.accountMode || 'business';
    }
    return 'business';
  } catch (error) {
    console.error('Error fetching account mode:', error);
    return 'business';
  }
}

/**
 * Check if user has a specific role OR is a Personal account
 * Personal accounts have full admin/manager privileges regardless of roles array
 * 
 * @param userId - The user ID to check
 * @param role - The role to check for ('admin', 'manager', 'crew_leader', 'technician')
 * @returns true if user has the role OR is a Personal account (for admin/manager checks)
 */
export async function hasRoleOrPersonalAccount(userId: string, role: AppRole): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data() as User;
    const accountMode = userData.accountMode || 'business';
    const roles = userData.roles || [];
    
    // Personal accounts have admin and manager privileges automatically
    if (accountMode === 'personal' && (role === 'admin' || role === 'manager')) {
      return true;
    }
    
    // Otherwise check if user explicitly has the role
    return roles.includes(role);
  } catch (error) {
    console.error('Error checking role or personal account:', error);
    return false;
  }
}

/**
 * Check if user is an employee (technician role only, NOT a Personal account owner)
 * This is useful for restricting access to certain features
 * 
 * @param userId - The user ID to check
 * @returns true if user is a technician employee (not a Personal account owner)
 */
export async function isEmployee(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data() as User;
    const accountMode = userData.accountMode || 'business';
    const roles = userData.roles || [];
    
    // Personal account owners are NOT employees even if they have technician role
    if (accountMode === 'personal') {
      return false;
    }
    
    // In business accounts, only technician role (without admin/manager) is considered employee
    return roles.includes('technician') && !roles.includes('admin') && !roles.includes('manager');
  } catch (error) {
    console.error('Error checking employee status:', error);
    return false;
  }
}

/**
 * Get comprehensive user access info
 * Returns account mode, roles, and computed permission flags
 * 
 * @param userId - The user ID to check
 * @returns Object with accountMode, roles, and permission flags
 */
export async function getUserAccessInfo(userId: string): Promise<{
  accountMode: AccountMode;
  roles: AppRole[];
  isPersonalAccount: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isCrewLeader: boolean;
  isEmployee: boolean;
}> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return {
        accountMode: 'business',
        roles: [],
        isPersonalAccount: false,
        isAdmin: false,
        isManager: false,
        isCrewLeader: false,
        isEmployee: false,
      };
    }
    
    const userData = userDoc.data() as User;
    const accountMode = userData.accountMode || 'business';
    const roles = userData.roles || [];
    const isPersonalAccount = accountMode === 'personal';
    
    return {
      accountMode,
      roles,
      isPersonalAccount,
      // Personal accounts get admin/manager privileges automatically
      isAdmin: roles.includes('admin') || isPersonalAccount,
      isManager: roles.includes('manager') || isPersonalAccount,
      isCrewLeader: roles.includes('crew_leader'),
      // Personal account owners are NOT considered employees
      isEmployee: !isPersonalAccount && roles.includes('technician') && !roles.includes('admin') && !roles.includes('manager'),
    };
  } catch (error) {
    console.error('Error getting user access info:', error);
    return {
      accountMode: 'business',
      roles: [],
      isPersonalAccount: false,
      isAdmin: false,
      isManager: false,
      isCrewLeader: false,
      isEmployee: false,
    };
  }
}

/**
 * Update user profile in Firestore
 */
export async function updateUserProfile(userId: string, data: Partial<User>) {
  try {
    await setDoc(doc(db, 'users', userId), {
      ...data,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { error: null };
  } catch (error: any) {
    return { error: { message: error.message } };
  }
}

/**
 * Send password reset email
 */
export async function resetPasswordForEmail(email: string) {
  try {
    await sendPasswordResetEmail(auth, email);
    return { data: null, error: null };
  } catch (error: any) {
    return { data: null, error: { message: error.message } };
  }
}

/**
 * Create or update user profile (for OAuth sign-ins like Apple)
 * New users get a minimal profile and profileCompleted: false
 */
export async function createOrUpdateUserProfile(uid: string, data: {
  fullName?: string;
  email?: string;
  userType?: string;
  accountMode?: 'personal' | 'business' | 'enterprise';
  tradeType?: TradeType;
  isServiceProvider?: boolean;
}) {
  try {
    const userRef = doc(db, 'users', uid);
    
    // Use setDoc with merge - this will create OR update
    // Avoids the getDoc() which might fail if JS SDK auth isn't synced yet
    const userData: Partial<User> & { profileCompleted?: boolean } = {
      uid: uid,
      email: data.email || '',
      fullName: data.fullName || '',
      tradeType: 'pending' as any, // Will be set in complete-profile
      accountMode: data.accountMode || 'personal',
      roles: [], // Will be set based on account mode in complete-profile
      profileCompleted: false, // Flag to indicate profile needs completion
      subscription: {
        status: 'none',
        plan: 'none',
        trialEndsAt: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        currentPeriodEnd: null,
      },
      updatedAt: serverTimestamp() as any,
    };

    // Use merge:true so createdAt only gets set once
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
    }, { merge: true });
    
    console.log('✅ Created/updated profile for OAuth user:', uid);
    
    return { error: null, isNewUser: true };
  } catch (error: any) {
    console.error('❌ Error creating user profile:', error);
    // Return the error but mark as new user so navigation continues
    return { error: { message: error.message }, isNewUser: true };
  }
}

/**
 * Subscribe to auth state changes
 * IMPORTANT: Always use JS SDK's onAuthStateChanged because with skipNativeAuth: true,
 * the native plugin doesn't track the JS SDK's auth state.
 * We sign in using signInWithCredential (JS SDK), so we must listen to JS SDK auth state.
 */
export function onAuthStateChange(callback: (user: FirebaseUser | null) => void) {
  console.log('🔔 Using JS SDK onAuthStateChanged (required for skipNativeAuth mode)...');
  return onAuthStateChanged(auth, callback);
}

// Export auth for direct use
export { auth };
